/**
 * Centralized Playback Event Bus (Sprint 6A).
 *
 * All providers publish through PlaybackEngine → this bus.
 * Analytics, watch history, resume, Experience Engine, and future plugins
 * subscribe here — never to provider-native events.
 *
 * Dual-publish: every canonical PlayerEvent also emits its PlaybackPlatformEvent
 * alias (and vice versa when publishing platform names) for backward compatibility.
 */
import { createEmitter } from "../Utilities/events";
import { PlayerEvent } from "./PlayerEvents";
import {
  PlaybackPlatformEvent,
  mapPlayerEventToPlatform,
  mapPlatformEventToPlayer,
} from "./PlaybackPlatformEvents";

var WILDCARD = "*";

/**
 * @returns {object} bus
 */
export function createPlaybackEventBus() {
  var emitter = createEmitter();
  var wildcard = [];

  function notifyWildcard(eventName, payload) {
    var list = wildcard.slice();
    for (var i = 0; i < list.length; i++) {
      try {
        list[i](eventName, payload);
      } catch (e) {
        if (typeof console !== "undefined" && console.error) {
          console.error("[PlaybackEventBus] subscriber error", e);
        }
      }
    }
  }

  /**
   * Publish a platform or legacy event. Dual-emits the mapped counterpart.
   * @param {string} eventName
   * @param {*} [payload]
   */
  function publish(eventName, payload) {
    if (!eventName) return;
    emitter.emit(eventName, payload);
    notifyWildcard(eventName, payload);

    var platform = mapPlayerEventToPlatform(eventName);
    if (platform && platform !== eventName) {
      emitter.emit(platform, payload);
      notifyWildcard(platform, payload);
    }

    var player = mapPlatformEventToPlayer(eventName);
    if (player && player !== eventName) {
      emitter.emit(player, payload);
      notifyWildcard(player, payload);
    }

    // Fullscreen / mute specializations
    if (eventName === PlayerEvent.FULLSCREEN_CHANGED) {
      var fsName = payload
        ? PlaybackPlatformEvent.FULLSCREEN_ENTER
        : PlaybackPlatformEvent.FULLSCREEN_EXIT;
      emitter.emit(fsName, payload);
      notifyWildcard(fsName, payload);
      var fsLegacy = payload ? "FullscreenEnter" : "FullscreenExit";
      emitter.emit(fsLegacy, payload);
      notifyWildcard(fsLegacy, payload);
    }

    if (
      eventName === PlayerEvent.VOLUME_CHANGED &&
      payload &&
      typeof payload.muted === "boolean" &&
      payload._muteChanged
    ) {
      emitter.emit(PlaybackPlatformEvent.MUTE_CHANGED, payload);
      notifyWildcard(PlaybackPlatformEvent.MUTE_CHANGED, payload);
      emitter.emit("MuteChanged", payload);
      notifyWildcard("MuteChanged", payload);
    }
  }

  /**
   * @param {string} eventName use "*" for all events
   * @param {function} fn
   * @returns {function} unsubscribe
   */
  function subscribe(eventName, fn) {
    if (eventName === WILDCARD) {
      wildcard.push(fn);
      return function () {
        wildcard = wildcard.filter(function (f) {
          return f !== fn;
        });
      };
    }
    return emitter.on(eventName, fn);
  }

  function unsubscribe(eventName, fn) {
    if (eventName === WILDCARD) {
      wildcard = wildcard.filter(function (f) {
        return f !== fn;
      });
      return;
    }
    emitter.off(eventName, fn);
  }

  function once(eventName, fn) {
    return emitter.once(eventName, fn);
  }

  function removeAllListeners() {
    wildcard = [];
    emitter.removeAllListeners();
  }

  // Compatibility with createEmitter consumers (engine.on / plugin host)
  function emit(eventName) {
    var args = Array.prototype.slice.call(arguments, 1);
    publish(eventName, args.length <= 1 ? args[0] : args);
  }

  return {
    publish: publish,
    subscribe: subscribe,
    unsubscribe: unsubscribe,
    once: once,
    on: subscribe,
    off: unsubscribe,
    emit: emit,
    removeAllListeners: removeAllListeners,
    /** @deprecated use subscribe("*", fn) */
    subscribeAll: function (fn) {
      return subscribe(WILDCARD, fn);
    },
  };
}

export { PlaybackPlatformEvent, WILDCARD as PLAYBACK_EVENT_WILDCARD };
export default createPlaybackEventBus;
